import React, { useState } from "react";
import { Link, usePage } from "@inertiajs/react";
import {
    LayoutDashboard,
    Users,
    User,
    MessageSquare,
    FileText,
    Briefcase,
    Mail,
    Settings,
    BarChart3,
    Calendar,
    ChevronLeft,
    ChevronDown,
    Globe,
    ArrowRightLeft,
    HelpCircle,
    Brain,
    Image as ImageIcon,
    UserPlus,
    Link2,
    Clapperboard,
    Handshake,
    Building2,
    UserRound,
    Kanban,
    ListChecks,
    MessagesSquare,
    Plug,
    Wallet,
    Receipt,
    MessageSquareText,
    MessageCircle,
    Bell,
    Send,
} from "lucide-react";

const Sidebar = ({ isOpen, toggleSidebar }) => {
    const { url, props } = usePage();
    const user = props.auth;
    const role = user?.role;
    const isOwner = user?.is_owner;
    const pendingCount = props.sidebarCounts?.pendingFreelancerApplications || 0;
    const [openGroups, setOpenGroups] = useState({
        crm:
            url.startsWith("/dashboard/crm") ||
            url.startsWith("/dashboard/settings/integrations") ||
            url.startsWith("/dashboard/settings/email-accounts"),
        whatsapp: url.startsWith("/dashboard/crm/whatsapp"),
        finance: url.startsWith("/dashboard/finance") || url.startsWith("/dashboard/invoice"),
        seo:
            url.startsWith("/dashboard/seo") ||
            url.startsWith("/dashboard/blogs") ||
            url.startsWith("/dashboard/faq") ||
            url.startsWith("/dashboard/redirects") ||
            url.startsWith("/dashboard/knowledge-base") ||
            url.startsWith("/dashboard/services"),
        gallery:
            url.startsWith("/dashboard/works") ||
            url.startsWith("/dashboard/case-studies") ||
            url.startsWith("/dashboard/showcase-reels"),
        settings: url.startsWith("/dashboard/users") || url.startsWith("/dashboard/client-logos"),
    });
    const toggleGroup = (key) => setOpenGroups((s) => ({ ...s, [key]: !s[key] }));

    const allMenuItems = [
        {
            title: "الرئيسية",
            icon: <LayoutDashboard className="w-5 h-5" />,
            href: "/dashboard",
            roles: ["all"],
        },
        {
            title: "الملف الشخصي",
            icon: <User className="w-5 h-5" />,
            href: "/dashboard/profile",
            roles: ["all"],
        },
        {
            title: "العملاء",
            icon: <Users className="w-5 h-5" />,
            href: "/dashboard/customers",
            roles: ["admin", "account_manager", "sales", "seo"],
        },
        {
            title: "إدارة العملاء (CRM)",
            icon: <Handshake className="w-5 h-5" />,
            roles: ["admin", "account_manager", "sales"],
            type: "group",
            key: "crm",
            children: [
                { title: "لوحة التحكم", href: "/dashboard/crm", icon: <LayoutDashboard className="w-4 h-4" /> },
                { title: "الشركات", href: "/dashboard/crm/companies", icon: <Building2 className="w-4 h-4" /> },
                { title: "جهات الاتصال", href: "/dashboard/crm/contacts", icon: <UserRound className="w-4 h-4" /> },
                { title: "الصفقات", href: "/dashboard/crm/deals", icon: <Kanban className="w-4 h-4" /> },
                { title: "المهام", href: "/dashboard/crm/tasks", icon: <ListChecks className="w-4 h-4" /> },
                { title: "المحادثات", href: "/dashboard/crm/conversations", icon: <MessagesSquare className="w-4 h-4" /> },
                { title: "التذكيرات والمتابعات", href: "/dashboard/crm/followups", icon: <Bell className="w-4 h-4" /> },
                { title: "روابط السيلز", href: "/dashboard/crm/sales-links", icon: <Link2 className="w-4 h-4" />, roles: ["admin"] },
                { title: "أداء المندوبين", href: "/dashboard/crm/sales-links/report", icon: <BarChart3 className="w-4 h-4" />, roles: ["admin"] },
                { title: "الحملات البريدية", href: "/dashboard/crm/campaigns", icon: <Mail className="w-4 h-4" /> },
                { title: "تيليجرام", href: "/dashboard/crm/telegram", icon: <Send className="w-4 h-4" /> },
                { title: "التكاملات", href: "/dashboard/settings/integrations", icon: <Plug className="w-4 h-4" /> },
                { title: "حسابات البريد", href: "/dashboard/settings/email-accounts", icon: <Mail className="w-4 h-4" /> },
            ],
        },
        {
            title: "واتساب بيزنس",
            icon: <MessageCircle className="w-5 h-5" />,
            roles: ["admin", "account_manager", "sales"],
            type: "group",
            key: "whatsapp",
            children: [
                { title: "نظرة عامة", href: "/dashboard/crm/whatsapp/overview", icon: <LayoutDashboard className="w-4 h-4" /> },
                { title: "الأرقام والإعدادات", href: "/dashboard/settings/integrations", icon: <Plug className="w-4 h-4" /> },
                { title: "القوالب", href: "/dashboard/crm/whatsapp-templates", icon: <MessageSquareText className="w-4 h-4" /> },
                { title: "حملات البث", href: "/dashboard/crm/whatsapp-campaigns", icon: <MessageCircle className="w-4 h-4" /> },
                { title: "صندوق المحادثات", href: "/dashboard/crm/conversations", icon: <MessagesSquare className="w-4 h-4" /> },
            ],
        },
        {
            title: "استفسارات العملاء",
            icon: <MessageSquare className="w-5 h-5" />,
            href: "/dashboard/contacts",
            roles: ["admin", "sales"],
        },
        {
            title: "الفريلانسرز",
            icon: <Briefcase className="w-5 h-5" />,
            href: "/dashboard/freelancers",
            roles: ["admin", "account_manager"],
        },
        {
            title: "المشاريع",
            icon: <Briefcase className="w-5 h-5" />,
            href: "/dashboard/projects",
            roles: ["admin", "account_manager", "sales"],
        },
        {
            title: "المالية",
            icon: <Wallet className="w-5 h-5" />,
            roles: ["admin", "account_manager", "sales"],
            type: "group",
            key: "finance",
            children: [
                { title: "لوحة التحكم المالية", href: "/dashboard/finance", icon: <LayoutDashboard className="w-4 h-4" /> },
                { title: "الفواتير", href: "/dashboard/invoices", icon: <FileText className="w-4 h-4" /> },
                { title: "المصروفات", href: "/dashboard/finance/expenses", icon: <Receipt className="w-4 h-4" /> },
            ],
        },
        {
            title: "السيو والمحتوى",
            icon: <Globe className="w-5 h-5" />,
            roles: ["admin", "seo"],
            type: "group",
            key: "seo",
            children: [
                { title: "إدارة السيو", href: "/dashboard/seo", icon: <Globe className="w-4 h-4" /> },
                { title: "المدونات", href: "/dashboard/blogs", icon: <FileText className="w-4 h-4" /> },
                { title: "الأسئلة الشائعة", href: "/dashboard/faq", icon: <HelpCircle className="w-4 h-4" /> },
                { title: "التحويلات (Redirects)", href: "/dashboard/redirects", icon: <ArrowRightLeft className="w-4 h-4" /> },
                { title: "قاعدة معرفة AI", href: "/dashboard/knowledge-base", icon: <Brain className="w-4 h-4" /> },
                { title: "الخدمات والربط الداخلي", href: "/dashboard/services", icon: <Link2 className="w-4 h-4" /> },
            ],
        },
        {
            title: "معرض الأعمال",
            icon: <ImageIcon className="w-5 h-5" />,
            roles: ["admin", "seo"],
            type: "group",
            key: "gallery",
            children: [
                { title: "الأعمال", href: "/dashboard/works", icon: <ImageIcon className="w-4 h-4" /> },
                { title: "دراسات الحالة", href: "/dashboard/case-studies", icon: <BarChart3 className="w-4 h-4" /> },
                { title: "معرض المنصات الاجتماعية", href: "/dashboard/showcase-reels", icon: <Clapperboard className="w-4 h-4" /> },
            ],
        },
        {
            title: "مشتركو النشرة البريدية",
            icon: <Mail className="w-5 h-5" />,
            href: "/dashboard/newsletter-subscribers",
            roles: ["admin", "seo"],
        },
        {
            title: "الإعدادات",
            icon: <Settings className="w-5 h-5" />,
            roles: ["owner"],
            type: "group",
            key: "settings",
            children: [
                { title: "المستخدمين", href: "/dashboard/users", icon: <Users className="w-4 h-4" /> },
                { title: "لوجوهات العملاء", href: "/dashboard/client-logos", icon: <ImageIcon className="w-4 h-4" /> },
            ],
        },
    ];

    const menuItems = allMenuItems.filter((item) => {
        if (isOwner) return true;
        if (item.roles.includes("all")) return true;
        if (item.roles.includes("owner")) return isOwner;
        return item.roles.includes(role);
    })
        // Child links may narrow access further with their own `roles`.
        .map((item) => (item.children && !isOwner
            ? { ...item, children: item.children.filter((c) => !c.roles || c.roles.includes(role)) }
            : item));

    const isActive = (href) => {
        if (["/dashboard", "/dashboard/crm", "/dashboard/finance"].includes(href)) return url === href;
        return url === href || url.startsWith(href + "/");
    };

    return (
        <>
            {/* Overlay for mobile */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={toggleSidebar}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed inset-y-0 right-0 z-50 flex h-screen w-[280px] shrink-0 flex-col border-l border-gray-200 bg-white shadow-xl transition-transform duration-200 ease-out lg:sticky lg:top-0 lg:z-20 lg:translate-x-0 lg:shadow-none ${
                    isOpen ? "translate-x-0" : "translate-x-full"
                }`}
            >
                {/* Logo */}
                <div className="flex h-[72px] shrink-0 items-center justify-between border-b border-gray-100 px-5">
                    <Link href="/dashboard" className="flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-600 text-lg font-extrabold text-white shadow-sm">
                            Wiz
                        </span>
                        <span className="text-sm font-semibold text-gray-700">لوحة الإدارة</span>
                    </Link>

                    <button
                        onClick={toggleSidebar}
                        aria-label="إغلاق القائمة"
                        className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 lg:hidden"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                </div>

                {/* Navigation */}
                <nav className="min-h-0 flex-1 overflow-y-auto p-3" aria-label="التنقل الرئيسي">
                    <ul className="space-y-1.5">
                        {menuItems.map((item, index) =>
                            item.type === "group" ? (
                                <li key={index}>
                                    <button
                                        type="button"
                                        onClick={() => toggleGroup(item.key)}
                                        aria-expanded={Boolean(openGroups[item.key])}
                                        className={`w-full flex items-center justify-between rounded-xl px-3 py-2.5 text-sm transition-colors ${
                                            item.children.some((c) => isActive(c.href))
                                                ? "bg-primary-50 text-primary-700 font-semibold"
                                                : "text-gray-700 hover:bg-gray-50"
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className={item.children.some((c) => isActive(c.href)) ? "text-primary-600" : "text-gray-500"}>{item.icon}</span>
                                            <span className="font-medium">{item.title}</span>
                                        </div>
                                        <ChevronDown
                                            className={`w-4 h-4 transition-transform ${
                                                openGroups[item.key] ? "rotate-180" : ""
                                            }`}
                                        />
                                    </button>
                                    {openGroups[item.key] && (
                                        <ul className="mr-5 mt-1.5 space-y-1 border-r border-gray-200 pr-2.5">
                                            {item.children.map((child, ci) => (
                                                <li key={ci}>
                                                    <Link
                                                        href={child.href}
                                                        onClick={() => isOpen && toggleSidebar()}
                                                        className={`flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] transition-colors ${
                                                            isActive(child.href)
                                                                ? "bg-primary-50 font-semibold text-primary-700"
                                                                : "text-gray-600 hover:bg-gray-50"
                                                        }`}
                                                    >
                                                        {child.icon}
                                                        {child.title}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            ) : (
                                <li key={index}>
                                    <Link
                                        href={item.href}
                                        onClick={() => isOpen && toggleSidebar()}
                                        className={`flex items-center justify-between rounded-xl px-3 py-2.5 text-sm transition-colors ${
                                            isActive(item.href)
                                                ? "bg-primary-50 font-semibold text-primary-700"
                                                : "text-gray-700 hover:bg-gray-50"
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            {item.icon}
                                            <span className="font-medium">
                                                {item.title}
                                            </span>
                                        </div>
                                        {item.badge && (
                                            <span className="px-2 py-1 text-xs font-semibold text-white bg-red-500 rounded-full">
                                                {item.badge}
                                            </span>
                                        )}
                                    </Link>
                                </li>
                            )
                        )}
                    </ul>
                </nav>
            </aside>
        </>
    );
};

export default Sidebar;
